import Vue from "vue";
import auth from "../auth";
import { setPendoFacility } from "./pendoFacility";

const store = {
  loaded: false,
  data: {
    config: {},
    clients: {},
    units: [],
    allUnits: [],
    beds: {},
    bedMap: {},
    symptoms: [],
    siteAndTypeOptions: [],
    symptomTypeMap: [],
    customSymptomTypeMap: {},
    pccIntegratedFacIds: new Set(),
    pccIntegratedFacIdsLoaded: false,
    staffIsolation: ["Mandatory Isolation", "Voluntary Isolation", "Symptomatic Isolation", "Work-to-Home Isolation", "None"],
    outcomes: [
      {
        value: "Chronic",
        label: "Chronic"
      },
      {
        value: "Deceased",
        label: "Deceased"
      },
      {
        value: "Discharged",
        label: "Discharged"
      },
      {
        value: "Hospitalized",
        label: "Hospitalized"
      },
      {
        value: "Resolved",
        label: "Resolved"
      }
    ]
  },
  init: function () {
    this.data.testData = "init";
    this.vue = new Vue();
    this.$http = this.vue.$http;
  },
  dateFormat: function () {
    let config = this.data.config.DateFormat || [];
    return config[0] || "DD-MM-YYYY";
  },
  datePickerDateFormat: function () {
    let config = this.data.config.DatePickerDateFormat || [];
    return config[0] || "dd-MM-yyyy";
  },
  adtEnabled: function () {
    let config = this.data.config.AdtSupport || [];
    return config[0] === "Y" || !auth.canModify();
  },
  pccIntegrationEnabled: function () {
    let config = this.data.config.PccIntegrationEnabled || [];
    return config[0] === "Y";
  },
  isPccFacility: function (facId) {
    if (!this.data.pccIntegratedFacIdsLoaded) {
      return this.pccIntegrationEnabled();
    }
    return this.data.pccIntegratedFacIds.has(Number(facId));
  },
  // tenant opted in to the PointClickCare Marketplace listing (config row PccMarketplaceEnabled = "Y", decision D1 in pcc-api)
  pccMarketplaceEnabled: function () {
    const config = this.data.config.PccMarketplaceEnabled || [];
    return this.pccIntegrationEnabled() && config[0] === "Y";
  },
  // progress-note write-back is offered only for PCC-integrated facilities of a Marketplace tenant;
  // fails closed (unlike isPccFacility) when the facility mappings did not load
  isPccMarketplaceFacility: function (facId) {
    return this.pccMarketplaceEnabled() && this.data.pccIntegratedFacIdsLoaded && this.data.pccIntegratedFacIds.has(Number(facId));
  },
  hasAnyNonPccFacility: function () {
    if (!this.pccIntegrationEnabled()) return true;
    if (!this.data.pccIntegratedFacIdsLoaded) return false;
    return (this.data.sites || []).some(s => !this.data.pccIntegratedFacIds.has(Number(s.id)));
  },
  hasAnyPccFacility: function () {
    if (!this.pccIntegrationEnabled()) return false;
    if (!this.data.pccIntegratedFacIdsLoaded) return true;
    return this.data.pccIntegratedFacIds.size > 0;
  },
  complianceAIEnabled: function () {
    let config = this.data.config.ComplianceAIEnabled || [];
    return config[0] === "Y";
  },
  rulesEngineEnabled: function () {
    let config = this.data.config.RulesEngineEnabled || [];
    return config[0] === "Y";
  },
  immunizationLocationRequired: function () {
    let config = this.data.config.ImmunizationLocRequired || [];
    return config[0] === "Y";
  },
  vaccineTypeRequired: function () {
    let config = this.data.config.ImmunizationVaccineTypeRequired || [];
    return config[0] === "Y";
  },
  treatmentProviderRequired: function () {
    let config = this.data.config.TreatmentProviderRequired || [];
    return config[0] === "Y";
  },
  treatmentDosageRequired: function () {
    let config = this.data.config.TreatmentDosageRequired || [];
    return config[0] === "Y";
  },
  treatmentFrequencyRequired: function () {
    let config = this.data.config.TreatmentFrequencyRequired || [];
    return config[0] === "Y";
  },
  treatmentRouteRequired: function () {
    let config = this.data.config.TreatmentRouteRequired || [];
    return config[0] === "Y";
  },
  disableFloorMap: function () {
    let config = this.data.config.DisableFloorMap || [];
    return config[0] === "Y";
  },
  infoLink: function () {
    let config = this.data.config.InfoLink || [];
    return config[0] || "/static/more-info.html";
  },
  staffIdAutoGen: function () {
    let config = this.data.config.staffIdAutoGen || [];
    return config[0] === "Y";
  },
  staffIdPrefix: function () {
    let config = this.data.config.staffIdPrefix || [];
    return config[0] || "";
  },
  labIdPrefix: function () {
    let config = this.data.config.labIdPrefix || [];
    return config[0] || "";
  },
  labAttributableUnitEnabled: function () {
    let config = this.data.config.LabAttributableUnitEnabled || [];
    return config[0] === "Y";
  },
  staffIsolation: function () {
    return this.data.staffIsolation || [];
  },
  hhOnly: function () {
    let config = this.data.config.hhOnly || [];
    return config[0] === "Y";
  },
  hhTarget: function () {
    let config = (this.data.config.hhTarget ?? []).at(0) ?? '90';
    return Number(config);
  },
  hhOutcomeCriteria: function () {
    let config = this.data.config.hhOutcomeCriteria || 0;
    console.log(config);
    if (config !== 0 && config[0]) {
      console.log(config[0]);
      try {
        config = JSON.parse(config[0]);
      } catch (e) {
        console.log(e);
        config = {};
      }
    }
    return config;
  },
  hhBelowTarget: function () {
    let config = (this.data.config.hhBelowTarget ?? []).at(0) ?? '75';
    return Number(config);
  },
  hhEmailReportsEnabled: function () {
    let config = this.data.config.HHEmailReportsEnabled || [];
    return config[0] === "Y";
  },
  maxEmailReports: function () {
    let config = (this.data.config.MaxReportSubscriptionGroups ?? []).at(0) ?? '3';
    return Number(config);
  },
  hhAssignedNameType: function () {
    const enabled = this.data.config.HandHygieneAssignedNameEnabled || [];
    const type = this.data.config.HandHygieneAssignedNameType || [];
    if (enabled.at(0) === "Y") {
      return type.at(0);
    } else {
      return "";
    }
  },
  hhModuleEnabled: function () {
    const enabled = this.data.config.HHModuleEnabled || [];
    return enabled.at(0) === "Y";
  },
  ppeModuleEnabled: function () {
    const enabled = this.data.config.PPEModuleEnabled || [];
    return enabled.at(0) === "Y";
  },
  utilizationReportEnabled: function () {
    const enabled = this.data.config.UtilizationReportEnabled || [];
    return enabled.at(0) === "Y";
  },
  patientIdType: function () {
    let config = this.data.config.AdtOccIdentifier || [];
    return config[0];
  },
  outbreakSummaryTemp: function () {
    let config = this.data.config.OutbreakSummaryTemp || [];
    return config[0];
  },
  outbreakSummaryTempSheet: function () {
    let config = this.data.config.OutbreakSummaryTempSheet || [];
    return config[0];
  },
  employeeTypes: function () {
    let config = this.data.config.EmployeeType || [];
    config.sort();
    return config;
  },
  logoutRedirect: function () {
    let config = this.data.config.LogoutRedirect || [];
    return config[0];
  },
  ISOriginRequired: function () {
    let config = this.data.config.ISOriginRequired || [];
    return config[0] === "Y";
  },
  loadConfigs: async function (done) {
    // console.log('loadUnits')
    var vm = this;
    const useNewEndpoints = await window.$getFeatureFlag('IPAC_CONFIGS_API_ROLLOUT');
    const url = useNewEndpoints ? `${window.CONFIG.legacy_facade_api}/configs` : window.CONFIG.api + "/configs/";

    this.$http
      .get(url, {
        headers: {
          Authorization: "Bearer " + auth.getToken()
        }
      })
      .then(response => {
        vm.data.config = response.body || {};

        let sortedLabProcedures = new Set(response.body.LabProcedures);
        sortedLabProcedures.delete("Unknown");
        sortedLabProcedures = Array.from(sortedLabProcedures.values()).sort();
        sortedLabProcedures.unshift("Unknown");
        vm.data.config.LabProcedures = sortedLabProcedures;

        const fallbackLabReasons = ["Symptomatic", "Contact", "Surveillance", "Not Selected"];
        const labReasonSource = Array.isArray(response.body.LabReason) && response.body.LabReason.length > 0
          ? response.body.LabReason
          : fallbackLabReasons;
        const sortedLabReasons = [...labReasonSource].sort();
        const notSelectedIdx = sortedLabReasons.indexOf("Not Selected");
        if (notSelectedIdx !== -1) {
          sortedLabReasons.splice(notSelectedIdx, 1);
          sortedLabReasons.push("Not Selected");
        }
        vm.data.config.LabReason = sortedLabReasons;

        let units = [];
        let allUnits = [];
        vm.data.sites.forEach(s => {
          let sUnits = s.units.sort((a, b) => {
            return a.displayText.localeCompare(b.displayText);
          });
          sUnits.forEach(u => {
            if (!u.site) {
              u.site = {
                id: s.id,
                displayText: s.displayText
              };
            }

            u.siteDisplayText = s.displayText;
            u.siteId = s.id;
            u.key = u.id + u.displayText;

            if (u.code !== "PLACE_HOLDER") {
              units.push(u);
            }
            allUnits.push(u);
          });
        });

        vm.data.units = units.sort(function (a, b) {
          return a.displayText.toLowerCase().localeCompare(b.displayText.toLowerCase());
        });
        vm.data.allUnits = allUnits.sort(function (a, b) {
          return a.displayText.toLowerCase().localeCompare(b.displayText.toLowerCase());
        });

        // Origins config: assign default if not present in config
        if (vm.data.config.InfectionCaseOrigins) {
          vm.data.origins = (vm.data.config.InfectionCaseOrigins || []).sort().map(origin => ({ label: origin, value: origin }));
        } else {
          vm.data.origins = [
            {
              value: "Community Acquired",
              label: "Community Acquired",
            },
            {
              value: "HAI",
              label: "HAI",
            },
            {
              value: "Other",
              label: "Other",
            }
          ];
        }

        console.log(units);
        console.log(allUnits);
        vm.loadSymptomTypes(done);
      });
  },
  loadCustomSypmtoms: function (id) {
    let vm = this;
    this.$http.get(window.CONFIG.legacy_facade_api + "/customsypmtoms/infectionsite/" + id).then(response => {
      let cSymptom = response.body || [];
      if (cSymptom && !cSymptom.isDeleted) {
        cSymptom.forEach(c => {
          c.custom = true;
        });
        vm.data.customSymptomTypeMap[id] = cSymptom;
      }
    });
  },
  loadSymptomTypes: async function (done) {
    var vm = this;

    const useNewEndpoints = await window.$getFeatureFlag('SYMPTOMS_ROLLOUT');
    const url = useNewEndpoints ? `${window.CONFIG.legacy_facade_api}/symptoms/0/classtypes?includeArchived=true` : window.CONFIG.api + "/symptoms/0/classtypes";

    this.$http.get(url).then(response => {
      let siteAndTypeOptions = [];
      siteAndTypeOptions.push({
        value: 0,
        label: "All Infection Sites"
      });

      var types = response.body || [];
      types.sort(function (a, b) {
        var compA = a.type;
        var compB = b.type;

        if (compA === "GI") compA = "a";
        else if (compA === "Respiratory") compA = "b";
        else if (compA === "UTI") compA = "c";
        else compA = "d" + a.displayText;

        if (compB === "GI") compB = "a";
        else if (compB === "Respiratory") compB = "b";
        else if (compB === "UTI") compB = "c";
        else compB = "d" + b.displayText;

        return compA.toLowerCase().localeCompare(compB.toLowerCase());
      });
      vm.data.symptomTypes = types;
      types.forEach(function (sType) {
        vm.loadCustomSypmtoms(sType.id);
        if (sType.type === "Common") {
          return;
        }
        vm.data.symptomTypeMap[sType.type] = sType;
        var children = [{ value: 0, label: "All Types" }];
        sType.subTypes.sort();
        sType.subTypes.forEach(function (subType) {
          children.push({
            value: subType,
            label: subType
          });
        });

        var option = {
          value: sType,
          label: sType.displayText
        };

        if (sType.subTypes && sType.subTypes.length > 0) {
          option.children = children;
        }
        siteAndTypeOptions.push(option);
      });

      Object.assign(vm.data.siteAndTypeOptions, siteAndTypeOptions);
      console.log("siteAndTypeOptions", vm.data);
      console.log("siteAndTypeOptions", vm.data.siteAndTypeOptions);
      vm.data.symptomTypes.props = {
        value: "displayText",
        children: "subTypes"
      };
      vm.loadSymptoms(done);
      console.log("vm.loadSymptoms()");
    });
  },
  loadSymptoms: async function (done) {
    var vm = this;
    const useNewEndpoints = await window.$getFeatureFlag('SYMPTOMS_ROLLOUT');
    const url = useNewEndpoints ? `${window.CONFIG.legacy_facade_api}/symptoms/all` : window.CONFIG.api + "/symptoms/all";

    this.$http.get(url).then(response => {
      vm.data.symptoms = response.body;
      vm.loadOrganisms(done);
    });
  },
  loadOrganisms: async function (done) {
    var vm = this;
    const useNewEndpoints = await window.$getFeatureFlag('IPAC_ORGANISMS_API_ROLLOUT');
    const url = useNewEndpoints ? `${window.CONFIG.legacy_facade_api}/organisms` : window.CONFIG.api + "/organisms/";

    this.$http.get(url).then(
      response => {
        let organisms = response.body["_embedded"].organisms;
        organisms.sort((a, b) => {
          let x = a.name.toLowerCase();
          let y = b.name.toLowerCase();
          if (x === "other") {
            x = "0";
          }
          if (y === "other") {
            y = "0";
          }
          if (x < y) {
            return -1;
          }
          if (x > y) {
            return 1;
          }
          return 0;
        });
        vm.data.organisms = organisms;
        vm.loadPrecautions(done);
      },
      response => {
        // error callback
      }
    );
  },
  loadPrecautions: async function (done) {
    var vm = this;
    const useNewEndpoints = await window.$getFeatureFlag('PRECAUTIONS_ROLLOUT');
    const url = useNewEndpoints ? `${window.CONFIG.legacy_facade_api}/precautions` : window.CONFIG.api + "/precautions/";

    this.$http.get(url).then(
      response => {
        vm.data.precautions = response.body["_embedded"].precautions;
        vm.loadTreatments(done);
      },
      response => {
        // error callback
      }
    );
  },
  loadTreatments: async function (done) {
    var vm = this;
    const useNewEndpoints = await window.$getFeatureFlag('TREATMENTS_API_ROLLOUT');
    const url = useNewEndpoints ? `${window.CONFIG.legacy_facade_api}/treatments` : window.CONFIG.api + "/treatments/";

    this.$http.get(url).then(
      response => {
        vm.data.treatments = response.body["_embedded"].treatments;
        vm.loadNotificationConfigs(done);
      },
      response => {
        // error callback
      }
    );
  },
  loadNotificationConfigs: function (done) {
    var vm = this;
    const url = `${window.CONFIG.infection_api}/notifications-config`;

    this.$http.get(url).then(
      response => {
        const configs = Array.isArray(response.body) ? response.body : [];
        vm.data.notificationConfigs = configs.filter(c => c.active);
        vm.loadPccFacilities(done);
      },
      response => {
        vm.data.notificationConfigs = [];
        vm.loadPccFacilities(done);
      }
    );
  },
  loadPccFacilities: function (done) {
    var vm = this;

    if (!vm.pccIntegrationEnabled()) {
      vm.data.pccIntegratedFacIds = new Set();
      vm.data.pccIntegratedFacIdsLoaded = true;
      if (done) {
        done();
      }
      vm.loaded = true;
      return;
    }

    this.$http.get(`${window.CONFIG.pcc_integration_api}/facilities`).then(
      response => {
        const ids = (response.body && response.body.pccIntegratedFacIds) || [];
        vm.data.pccIntegratedFacIds = new Set(ids.map(Number));
        vm.data.pccIntegratedFacIdsLoaded = true;
        if (done) {
          done();
        }
        vm.loaded = true;
      },
      err => {
        console.error("Failed to load PCC facility mapping; falling back to org-wide PCC behavior.", err);
        vm.data.pccIntegratedFacIds = new Set();
        vm.data.pccIntegratedFacIdsLoaded = false;
        if (done) {
          done();
        }
        vm.loaded = true;
      }
    );
  },
  getClientName: function (clientData) {
    var firstName = clientData.firstName ? clientData.firstName + " " : "";
    var middleName = clientData.middleName ? clientData.middleName + " " : "";
    var lastName = clientData.lastName ? clientData.lastName : "";

    // console.log(JSON.stringify(clientData, ' ', 2))
    return firstName + middleName + lastName;
  },
  loadAll: function (done, useNewEndpoint) {
    let vm = this;

    const orgEndpoint = useNewEndpoint
      ? `${window.CONFIG.legacy_facade_api}/organization?appType=web`
      : `${window.CONFIG.api}/organization/list?appType=web`;

    vm.$http.get(orgEndpoint).then(
      response => {
        if (response.body) {
          vm.data.organization = response.body;
          if (vm.data.organization[0]) {
            vm.data.organization[0].sites = vm.data.organization[0].sites.sort((a, b) => {
              return a.displayText.localeCompare(b.displayText);
            });
            vm.data.sites = vm.data.organization[0].sites;
            const facility = `${vm.data.sites.length > 1 ? 'multi-site' : vm.data.sites[0]?.displayText ?? vm.data.organization[0]?.displayText}`;
            pendo.updateOptions({
              visitor: {
                facility
              }
            });
            setPendoFacility(facility);
          } else {
            vm.data.organization = [];
            vm.data.sites = [];
            setPendoFacility(undefined);
          }
        } else {
          vm.data.organization = [];
          setPendoFacility(undefined);
        }

        // vm.data.bedMap[bed.id] = bed
        // sort sites
        vm.data.sites.forEach(s => {
          (s.units || []).forEach(u => {
            (u.rooms || []).forEach(r => {
              (r.beds || []).forEach(b => {
                if (b) {
                  vm.data.bedMap[b.id] = b;
                  vm.data.beds[b.occId] = r.displayText + "-" + b.displayText;
                }
              });
            });
          });
        });

        let finished = function () {
          if (done) {
            done();
          }
          vm.dataReady = true;
        };

        vm.loadConfigs(finished);
      },
      response => {
        // error callback
        setPendoFacility(undefined);
      }
    );
    // }, response => {
    //   // error callback
    // })
  },
  labResults: function () {
    const config = this.data.config.LabResults || [];
    return config.sort().map(r => r.slice(r.indexOf("_") + 1));
  },
  getLineListReports() {
    return {
      infectionSurveillance: "IS",
      outbreakManagement: "OM",
      antimicrobialTimeout: "AMTO",
      medicationUsage: "MEDUSE",
      summaryInfection: "SUMINF"
    };
  },
  getConfigFlag(key) {
    const { Features } = this.data.config;
    const featureFlags = Features || [];
    const lookup = Object.fromEntries([...featureFlags.entries()].map(([i, flagName]) => [flagName, true]));
    return lookup[key] || false;
  }
};

store.install = function (Vue) {
  store.init();
  Object.defineProperty(Vue.prototype, "$configStore", {
    get() {
      return store;
    }
  });
};

export default store;
